import { supabase } from '@/lib/supabase'
import type { Profile, ProfileUpdate } from '@/types/database'

// Short-lived cache for org_id to avoid repeated DB round-trips
// within the same React render cycle / batch of service calls.
let _orgIdCache: { value: string | null; expiry: number } | null = null
let _orgIdPendingRequest: Promise<string | null> | null = null
const ORG_ID_CACHE_MS = 5_000 // 5 seconds

export const profileService = {
  /**
   * Get the current user's profile with organization
   */
  async getCurrentProfile(): Promise<Profile | null> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return null

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single()

    if (error) {
      console.error('Error fetching profile:', error)
      return null
    }

    return data
  },

  /**
   * Get user's organization ID (cached for 5s to avoid repeated queries).
   * Deduplicates concurrent requests to prevent redundant DB calls.
   */
  async getOrganizationId(): Promise<string | null> {
    if (_orgIdCache && Date.now() < _orgIdCache.expiry) {
      return _orgIdCache.value
    }

    // Deduplicate concurrent requests
    if (_orgIdPendingRequest) {
      return _orgIdPendingRequest
    }

    _orgIdPendingRequest = this.getCurrentProfile()
      .then((profile) => {
        const value = profile?.organization_id ?? null
        _orgIdCache = { value, expiry: Date.now() + ORG_ID_CACHE_MS }
        return value
      })
      .finally(() => {
        _orgIdPendingRequest = null
      })

    return _orgIdPendingRequest
  },

  /** Invalidate the org_id cache (call after org transfer) */
  clearOrgIdCache() {
    _orgIdCache = null
  },

  /**
   * Update the current user's profile
   */
  async updateProfile(updates: ProfileUpdate): Promise<Profile | null> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return null

    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id)
      .select()
      .single()

    if (error) {
      console.error('Error updating profile:', error)
      throw error
    }

    return data
  },

  /**
   * Get all profiles in the user's organization
   */
  async getOrganizationProfiles(): Promise<Profile[]> {
    const orgId = await this.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('organization_id', orgId)
      .order('full_name')

    if (error) {
      console.error('Error fetching organization profiles:', error)
      return []
    }

    return data ?? []
  },
}
